import { CKSelectableItem, CKSelectButton } from 'clearkit';

import { CaptureProspects } from './CaptureProspects';
import { ErrorBoundary } from './ErrorBoundary';
import {
  FilterSelectItem,
  FilterValue,
  useProspectFilters,
} from './useProspectFilters';
import { useProspects } from './useProspects';

type CapturePreviewProps = {
  domain: string;
};

function CapturePreviewInternal({ domain }: CapturePreviewProps) {
  const {
    role,
    roleItems,
    seniority,
    seniorityItems,
    update,
    loading,
  } = useProspectFilters();
  const { data } = useProspects(domain, { role, seniority });

  const contactCount = data?.totalCount ?? 0;
  const contactLabel = contactCount === 1 ? 'contact' : 'contacts';
  const selectedSeniority = getItem(seniorityItems, seniority);
  const selectedRole = getItem(roleItems, role);

  if (loading) {
    return null;
  }

  return (
    <div className="p-6 pb-0 mb-4">
      <header className="mb-6">
        <p className="mb-4 text-base">
          Clearbit Capture found {contactCount} {contactLabel} at this company
          based on the criteria below.{' '}
          <a
            className="font-semibold text-gray-800"
            href="https://clearbit.com/blog/clearbit-capture"
          >
            Learn more about Clearbit Capture
          </a>
          .
        </p>
        <div className="flex items-center text-sm gap-2">
          Showing
          <CKSelectButton
            items={seniorityItems}
            onChange={(newValue: CKSelectableItem) =>
              update('seniority', newValue.value)
            }
            placeholder="seniority"
            popoverWidth="20ch"
            triggerVariantColor="blue"
            value={selectedSeniority}
          />
          level in
          <CKSelectButton
            items={roleItems}
            onChange={(newValue: CKSelectableItem) =>
              update('role', newValue.value)
            }
            placeholder="role"
            popoverWidth="20ch"
            triggerVariantColor="blue"
            value={selectedRole}
          />
        </div>
      </header>
      <CaptureProspects prospects={data?.nodes} />
    </div>
  );
}

export function CapturePreview(props: CapturePreviewProps) {
  return (
    <ErrorBoundary>
      <CapturePreviewInternal {...props} />
    </ErrorBoundary>
  );
}

function getItem(items: FilterSelectItem[], query: string) {
  return items
    .filter((item): item is FilterValue => 'label' in item && !!item.label)
    .find((item) => item.value === query);
}
